import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Telescope, Search, ExternalLink, Building2, MapPin, Tag,
  Loader2, TrendingUp, Lightbulb, Sparkles, Globe, Users,
  Calendar, ChevronRight, Layers,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function DetailModal({ entry, open, onClose }) {
  if (!entry) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto embed-light">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center flex-shrink-0">
              {String(entry.type || "").toLowerCase().includes("startup") || (Array.isArray(entry.type) && entry.type.some(t => String(t).toLowerCase().includes("startup")))
                ? <Lightbulb className="w-6 h-6 text-emerald-600" />
                : <Sparkles className="w-6 h-6 text-emerald-600" />}
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-xl leading-tight">{entry.name}</DialogTitle>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {entry.category && <Badge className="bg-emerald-100 text-emerald-700 text-xs">{entry.category}</Badge>}
                {entry.type && (Array.isArray(entry.type) ? entry.type : [entry.type]).map((t, i) => <Badge key={i} variant="outline" className="text-xs">{String(t)}</Badge>)}
              </div>
            </div>
          </div>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {entry.description && (
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Descripcion</p>
              <p className="text-sm leading-relaxed text-gray-700 whitespace-pre-wrap">{entry.description}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            {entry.sector && (
              <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                <Building2 className="w-4 h-4 text-gray-400 mt-0.5" />
                <div><p className="text-xs text-gray-400">Sector</p><p className="text-sm font-medium">{Array.isArray(entry.sector) ? entry.sector.join(", ") : entry.sector}</p></div>
              </div>
            )}
            {entry.location && (
              <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                <div><p className="text-xs text-gray-400">Ubicacion</p><p className="text-sm font-medium">{entry.location}</p></div>
              </div>
            )}
            {entry.employees && (
              <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                <Users className="w-4 h-4 text-gray-400 mt-0.5" />
                <div><p className="text-xs text-gray-400">Empleados</p><p className="text-sm font-medium">{entry.employees}</p></div>
              </div>
            )}
            {entry.founded && (
              <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
                <div><p className="text-xs text-gray-400">Fundada</p><p className="text-sm font-medium">{entry.founded}</p></div>
              </div>
            )}
            {entry.status && (
              <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                <Tag className="w-4 h-4 text-gray-400 mt-0.5" />
                <div><p className="text-xs text-gray-400">Estado</p><p className="text-sm font-medium">{Array.isArray(entry.status) ? entry.status.join(", ") : entry.status}</p></div>
              </div>
            )}
          </div>
          {entry.tags && entry.tags.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Tags</p>
              <div className="flex flex-wrap gap-1.5">{entry.tags.map((tag, i) => <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>)}</div>
            </div>
          )}
          {entry.url && (
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" asChild>
              <a href={entry.url} target="_blank" rel="noopener noreferrer"><Globe className="w-4 h-4 mr-2" />Visitar web</a>
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EntryCard({ entry, onClick }) {
  const sector = Array.isArray(entry.sector) ? entry.sector.join(", ") : String(entry.sector || entry.category || "General");
  let sectorColor = "bg-blue-100 text-blue-700";
  const sl = sector.toLowerCase();
  if (sl.includes("digital") || sl.includes("tech")) sectorColor = "bg-purple-100 text-purple-700";
  if (sl.includes("energy") || sl.includes("energ") || sl.includes("h2")) sectorColor = "bg-green-100 text-green-700";
  if (sl.includes("ia") || sl.includes("ai")) sectorColor = "bg-amber-100 text-amber-700";

  return (
    <Card className="hover:shadow-md transition-all cursor-pointer group" onClick={onClick} data-testid={`obs-embed-entry-${entry.id}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <CardTitle className="text-base leading-tight group-hover:text-emerald-700 transition-colors">{entry.name}</CardTitle>
          {entry.url && (
            <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-emerald-500 transition-colors flex-shrink-0" />
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {entry.description && <p className="text-sm text-gray-600 line-clamp-2">{entry.description}</p>}
        <div className="flex flex-wrap gap-1.5">
          <Badge className={sectorColor + " text-xs"}>{sector}</Badge>
          {entry.type && (Array.isArray(entry.type) ? entry.type.slice(0, 1) : [entry.type]).map((t, i) => <Badge key={i} variant="outline" className="text-xs">{String(t)}</Badge>)}
          {entry.location && <span className="flex items-center gap-1 text-xs text-gray-500"><MapPin className="w-3 h-3" />{entry.location}</span>}
        </div>
        <div className="flex items-center text-xs text-gray-400 group-hover:text-emerald-600 transition-colors pt-1">
          <ChevronRight className="w-3.5 h-3.5 mr-1" />Ver ficha completa
        </div>
      </CardContent>
    </Card>
  );
}

export default function ObservatorioEmbedPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [detailEntry, setDetailEntry] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    fetch(API + "/api/public/observatorio")
      .then(r => r.ok ? r.json() : [])
      .then(data => setEntries(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const categories = [...new Set(entries.map(e => e.category).filter(Boolean))].sort();
  const types = [...new Set(entries.flatMap(e => Array.isArray(e.type) ? e.type.map(String) : (e.type ? [String(e.type)] : [])).filter(Boolean))].sort();

  const filtered = entries.filter(e => {
    const term = searchTerm.toLowerCase();
    const matchSearch = !searchTerm ||
      (e.name || "").toLowerCase().includes(term) ||
      (e.description || "").toLowerCase().includes(term) ||
      (Array.isArray(e.sector) ? e.sector.join(" ") : (e.sector || "")).toLowerCase().includes(term);
    const matchCat = categoryFilter === "all" || e.category === categoryFilter;
    const entryTypes = Array.isArray(e.type) ? e.type.map(String) : (e.type ? [String(e.type)] : []);
    const matchType = typeFilter === "all" || entryTypes.includes(typeFilter);
    return matchSearch && matchCat && matchType;
  });

  const startups = entries.filter(e => e.category === "Startups/Scaleups").length;
  const corporates = entries.filter(e => e.category === "Corporates").length;
  const technologies = entries.filter(e => {
    const t = Array.isArray(e.type) ? e.type.join(" ") : String(e.type || "");
    return t.toLowerCase().includes("tech") || t.toLowerCase().includes("tecno");
  }).length;

  if (loading) return <div className="flex items-center justify-center h-screen bg-gray-50 embed-light"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6 embed-light" data-testid="observatorio-embed">
      <div className="max-w-7xl mx-auto space-y-5">
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-6 text-white">
          <div className="flex items-center gap-3 mb-2"><Telescope className="w-8 h-8" /><h1 className="text-2xl font-bold">Observatorio Tecnologico</h1></div>
          <p className="text-emerald-100 text-sm">Valoriza - Ecosistema de Innovacion</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="text-center p-4 bg-white rounded-xl shadow-sm border">
            <Building2 className="w-5 h-5 mx-auto mb-1 text-blue-600" />
            <p className="text-2xl font-bold">{entries.length}</p>
            <p className="text-xs text-gray-500">Total</p>
          </div>
          <div className="text-center p-4 bg-white rounded-xl shadow-sm border">
            <Lightbulb className="w-5 h-5 mx-auto mb-1 text-amber-600" />
            <p className="text-2xl font-bold">{startups}</p>
            <p className="text-xs text-gray-500">Startups</p>
          </div>
          <div className="text-center p-4 bg-white rounded-xl shadow-sm border">
            <Building2 className="w-5 h-5 mx-auto mb-1 text-indigo-600" />
            <p className="text-2xl font-bold">{corporates}</p>
            <p className="text-xs text-gray-500">Corporates</p>
          </div>
          <div className="text-center p-4 bg-white rounded-xl shadow-sm border">
            <TrendingUp className="w-5 h-5 mx-auto mb-1 text-green-600" />
            <p className="text-2xl font-bold">{technologies}</p>
            <p className="text-xs text-gray-500">Tecnologias</p>
          </div>
          <div className="text-center p-4 bg-white rounded-xl shadow-sm border">
            <Layers className="w-5 h-5 mx-auto mb-1 text-purple-600" />
            <p className="text-2xl font-bold">{categories.length}</p>
            <p className="text-xs text-gray-500">Categorias</p>
          </div>
        </div>

        {/* Filters */}
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input placeholder="Buscar por nombre, descripcion o sector..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 bg-white" />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px] bg-white"><SelectValue placeholder="Tipo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                {types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant={categoryFilter === "all" ? "default" : "outline"} size="sm" className={categoryFilter === "all" ? "bg-emerald-600 hover:bg-emerald-700" : ""} onClick={() => setCategoryFilter("all")}>Todos</Button>
            {categories.map(cat => (
              <Button key={cat} variant={categoryFilter === cat ? "default" : "outline"} size="sm" className={categoryFilter === cat ? "bg-emerald-600 hover:bg-emerald-700" : ""} onClick={() => setCategoryFilter(cat)}>{cat}</Button>
            ))}
          </div>
        </div>

        <p className="text-xs text-gray-400">{filtered.length} resultados</p>

        {/* Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((entry, i) => (
            <EntryCard key={entry.id || i} entry={entry} onClick={() => { setDetailEntry(entry); setDetailOpen(true); }} />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <Telescope className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No se encontraron resultados</p>
          </div>
        )}

        <DetailModal entry={detailEntry} open={detailOpen} onClose={setDetailOpen} />

        <div className="text-center text-xs text-gray-400 pt-4">Innovation OS - Valoriza</div>
      </div>
    </div>
  );
}
